import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { useImage } from 'react-image';
import { useTranslation } from 'react-i18next';
import OCGatewayClient from '../client/OCGatewayClient';
import { DeviceEvent } from '../models/DeviceEvent';
import styles from './EventNavCarousel.module.css';

const POSTER_FRAME_PLACEHOLDER_SRC = '/assets/event/placeholder.jpg';
const LOAD_OLDER_SCROLL_THRESHOLD = 200;
// how long scrolling must be quiet before the gesture counts as finished
const SCROLL_SETTLE_MS = 150;
// caps how often a fling can swap the page data while still feeling live
const SCRUB_NAVIGATE_MS = 80;

interface EventNavCarouselProps {
    /** Device events, newest first */
    events: DeviceEvent[];
    currentIndex: number;
    onNavigate: (eventId: number) => void;
    /** Fetch the next (older) page when the user swipes to the left edge */
    onLoadOlder?: () => void;
    canLoadOlder?: boolean;
}

const CarouselThumb = ({ event, className }: { event: DeviceEvent, className: string }) => {
    const posterFrameImage = useImage({
        srcList: [`${OCGatewayClient.apiURL}/events/${event.deviceId}/${event.eventId}/${event.posterFrameIndex || 0}`],
        useSuspense: false
    });

    const src = posterFrameImage.error || posterFrameImage.isLoading
        ? POSTER_FRAME_PLACEHOLDER_SRC
        : posterFrameImage.src;

    return (
        <div className={className}>
            <img src={src} alt="" />
        </div>
    );
};

// calendar day an event belongs to, or undefined when there is no usable
// timestamp — boundaries only draw between two known days
const eventDayKey = (event: DeviceEvent) => {
    if (!event.timestamp) {
        return undefined;
    }

    const timestamp = new Date(event.timestamp);

    return Number.isNaN(timestamp.getTime()) ? undefined : format(timestamp, 'yyyy-MM-dd');
};

const formatEventTime = (event: DeviceEvent) => {
    if (!event.timestamp) {
        return `#${event.eventId}`;
    }

    const timestamp = new Date(event.timestamp);

    return Number.isNaN(timestamp.getTime()) ? `#${event.eventId}` : format(timestamp, 'HH:mm');
};

/**
 * Fixed bottom filmstrip over the event modal content: every loaded event as a
 * thumb in a horizontally swipeable strip (oldest left, newest right). Scrolling
 * scrubs the page data live — each thumb reaching the centre ticks a light haptic
 * and swaps the event being shown; tapping a thumb jumps straight to it, and
 * swiping to the left edge loads older events.
 */
const EventNavCarousel: React.FC<EventNavCarouselProps> = ({ events, currentIndex, onNavigate, onLoadOlder, canLoadOlder }) => {
    const { t } = useTranslation('pages');
    const scrollerRef = useRef<HTMLDivElement>(null);
    const currentSlotRef = useRef<HTMLButtonElement>(null);
    const hasCentered = useRef(false);
    const prevScrollWidth = useRef(0);
    const prevLeftmostEventId = useRef<number | undefined>(undefined);
    // scroll bookkeeping: ignore our own scrolling, and don't re-centre the
    // strip out from under a finger that is mid-scrub
    const isProgrammaticScroll = useRef(false);
    const isPointerDown = useRef(false);
    const isScrubbing = useRef(false);
    const settleTimeout = useRef<number | undefined>(undefined);
    const scrubTimeout = useRef<number | undefined>(undefined);
    const lastScrubAt = useRef(0);
    const centredEventId = useRef<number | undefined>(undefined);
    const currentEventIdRef = useRef<number | undefined>(undefined);

    const currentEventId = events[currentIndex]?.eventId;
    currentEventIdRef.current = currentEventId;

    // oldest → newest, left → right
    const orderedEvents = useMemo(() => [...events].reverse(), [events]);

    // older pages grow the strip on the left; shift scroll so the view stays put
    useLayoutEffect(() => {
        const scroller = scrollerRef.current;

        if (!scroller) {
            return;
        }

        const leftmostEventId = orderedEvents[0]?.eventId;

        // compensation preserves which thumb is centred, so it needs no tick
        // suppression — the centred-thumb check simply won't change
        if (prevLeftmostEventId.current !== undefined && leftmostEventId !== prevLeftmostEventId.current) {
            scroller.scrollLeft += scroller.scrollWidth - prevScrollWidth.current;
        }

        prevLeftmostEventId.current = leftmostEventId;
        prevScrollWidth.current = scroller.scrollWidth;
    }, [orderedEvents]);

    // keep the viewed event centred: smooth after navigation, and on first
    // presentation retry across frames — the modal's present animation and the
    // snap container's initial layout can reset an early scroll position
    useLayoutEffect(() => {
        // mid-scrub the finger owns the scroll position, and snapping already
        // holds the centred thumb in place
        if (isScrubbing.current) {
            centredEventId.current = currentEventId;
            return;
        }

        let frame = 0;
        let attempts = 0;

        const center = () => {
            const scroller = scrollerRef.current;
            const currentSlot = currentSlotRef.current;

            if (!scroller || !currentSlot) {
                return;
            }

            const target = Math.max(0, currentSlot.offsetLeft - (scroller.clientWidth - currentSlot.offsetWidth) / 2);

            if (scroller.clientWidth > 0 && Math.abs(scroller.scrollLeft - target) < 2) {
                hasCentered.current = true;
                centredEventId.current = currentEventId;
                isProgrammaticScroll.current = false;
                return;
            }

            isProgrammaticScroll.current = true;

            if (hasCentered.current) {
                scroller.scrollTo({ left: target, behavior: 'smooth' });
                return;
            }

            scroller.scrollLeft = target;

            if (attempts++ < 60) {
                frame = requestAnimationFrame(center);
            } else {
                hasCentered.current = true;
            }
        };

        center();

        return () => cancelAnimationFrame(frame);
    }, [currentEventId]);

    const findCentredEventId = useCallback(() => {
        const scroller = scrollerRef.current;

        if (!scroller) {
            return undefined;
        }

        const viewportCentre = scroller.scrollLeft + scroller.clientWidth / 2;
        let closestEventId: number | undefined;
        let closestDistance = Infinity;

        for (const slot of Array.from(scroller.children) as HTMLElement[]) {
            const eventId = Number(slot.dataset.eventId);

            if (!Number.isFinite(eventId)) {
                continue;
            }

            const distance = Math.abs(slot.offsetLeft + slot.offsetWidth / 2 - viewportCentre);

            if (distance < closestDistance) {
                closestDistance = distance;
                closestEventId = eventId;
            }
        }

        return closestEventId;
    }, []);

    // swap the shown event as the strip scrubs past it, throttled so a hard
    // fling can't fire a request per thumb
    const scrubToEvent = useCallback((eventId: number) => {
        if (eventId === currentEventIdRef.current) {
            return;
        }

        window.clearTimeout(scrubTimeout.current);
        const elapsed = performance.now() - lastScrubAt.current;

        if (elapsed >= SCRUB_NAVIGATE_MS) {
            lastScrubAt.current = performance.now();
            onNavigate(eventId);
            return;
        }

        // trailing call re-reads the centre, so it lands on wherever we are by then
        scrubTimeout.current = window.setTimeout(() => {
            const latestEventId = findCentredEventId();

            if (latestEventId !== undefined && latestEventId !== currentEventIdRef.current) {
                lastScrubAt.current = performance.now();
                onNavigate(latestEventId);
            }
        }, SCRUB_NAVIGATE_MS - elapsed);
    }, [findCentredEventId, onNavigate]);

    const handleScrollSettled = useCallback(() => {
        // still dragging: the pointer-up handler re-arms this
        if (isPointerDown.current) {
            return;
        }

        isProgrammaticScroll.current = false;
        isScrubbing.current = false;

        // reconcile: guarantees the page matches the thumb we came to rest on
        const restingEventId = findCentredEventId();

        if (restingEventId !== undefined && restingEventId !== currentEventIdRef.current) {
            onNavigate(restingEventId);
        }
    }, [findCentredEventId, onNavigate]);

    const handleScroll = useCallback(() => {
        const scroller = scrollerRef.current;

        if (!scroller) {
            return;
        }

        if (canLoadOlder && scroller.scrollLeft < LOAD_OLDER_SCROLL_THRESHOLD) {
            onLoadOlder?.();
        }

        const nextCentredEventId = findCentredEventId();

        if (nextCentredEventId !== undefined && nextCentredEventId !== centredEventId.current) {
            centredEventId.current = nextCentredEventId;

            // picker-style tick plus a live data swap, for user scrolling only
            if (!isProgrammaticScroll.current) {
                isScrubbing.current = true;
                Haptics.impact({ style: ImpactStyle.Light }).catch(() => undefined);
                scrubToEvent(nextCentredEventId);
            }
        }

        window.clearTimeout(settleTimeout.current);
        settleTimeout.current = window.setTimeout(handleScrollSettled, SCROLL_SETTLE_MS);
    }, [canLoadOlder, findCentredEventId, handleScrollSettled, onLoadOlder, scrubToEvent]);

    const handlePointerDown = useCallback(() => {
        isPointerDown.current = true;
        // the user is driving now, so stop treating scrolling as ours
        isProgrammaticScroll.current = false;
    }, []);

    const handlePointerUp = useCallback(() => {
        isPointerDown.current = false;
        window.clearTimeout(settleTimeout.current);
        settleTimeout.current = window.setTimeout(handleScrollSettled, SCROLL_SETTLE_MS);
    }, [handleScrollSettled]);

    useEffect(() => () => {
        window.clearTimeout(settleTimeout.current);
        window.clearTimeout(scrubTimeout.current);
    }, []);

    if (!currentEventId) {
        return null;
    }

    return (
        <div slot="fixed" className={styles.bar}>
            <div
                ref={scrollerRef}
                className={styles.scroller}
                onScroll={handleScroll}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
            >
                {orderedEvents.map((event, index) => {
                    const isCurrent = event.eventId === currentEventId;
                    const previousDay = index > 0 ? eventDayKey(orderedEvents[index - 1]) : undefined;
                    const day = eventDayKey(event);
                    const crossesDay = !!previousDay && !!day && day !== previousDay;

                    return (
                        <Fragment key={event.eventId}>
                            {/* day boundary; not a slot, so the centring scan skips it */}
                            {crossesDay && <div className={styles.dayDivider} aria-hidden="true" />}
                            <button
                                ref={isCurrent ? currentSlotRef : undefined}
                                type="button"
                                data-event-id={event.eventId}
                                className={styles.slot}
                                aria-label={`${t('event.event_number', { eventId: event.eventId })} · ${formatEventTime(event)}`}
                                aria-current={isCurrent || undefined}
                                onClick={() => {
                                    if (!isCurrent) {
                                        onNavigate(event.eventId);
                                    }
                                }}
                            >
                                <div className={styles.thumbBox}>
                                    <CarouselThumb
                                        event={event}
                                        className={`${styles.thumb} ${isCurrent ? styles.currentThumb : styles.neighborThumb}`}
                                    />
                                </div>
                                <span className={`${styles.caption} ${isCurrent ? styles.captionCurrent : ''}`}>
                                    {formatEventTime(event)}
                                </span>
                            </button>
                        </Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export default EventNavCarousel;
